import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Chip, Dialog, DialogActions, DialogContent, IconButton, InputAdornment, MenuItem, Pagination, Skeleton, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { MdAdd, MdClose, MdDeleteOutline, MdEdit, MdInventory2, MdSearch, MdVisibility } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { deleteCombo, getAllCombos, updateCombo } from '../../Webservice/offerAPI';
import { FONT, STATE, StateChip, imageUrl, period, stateOf, yellowBtn } from './bogoShared';

const LIMIT = 15;
const head = { fontFamily: FONT, fontWeight: 800, fontSize: 12.5, color: '#475569', background: '#f8fafc', whiteSpace: 'nowrap' };
const cell = { fontFamily: FONT, fontSize: 13.5 };
const iconBtn = (c) => ({ color: c, background: `${c}14`, borderRadius: '8px', '&:hover': { background: `${c}26` } });
const inr = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const Kpi = ({ title, value, accent, active, onClick }) => (
  <Box onClick={onClick} sx={{ flex: 1, minWidth: 150, p: 2, borderRadius: '16px', color: '#fff', background: `linear-gradient(135deg,${accent},${accent}cc)`, cursor: onClick ? 'pointer' : 'default', outline: active ? '3px solid #0f172a' : 'none', outlineOffset: 2 }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 12.5, opacity: 0.9, fontWeight: 700 }}>{title}</Typography>
    <Typography sx={{ fontFamily: FONT, fontSize: 26, fontWeight: 800, lineHeight: 1.15 }}>{value}</Typography>
  </Box>
);

// MRP: the typed one, else the sum of the products' own MRPs
const mrpOf = (c) => c.b2c_mrp || (c.products || []).reduce((a, p) => a + ((p.product_id?.b2c_mrp || 0) * (p.quantity || 1)), 0) || 0;

function ComboOfferList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ total: 0, totalPages: 1 });
  const [stats, setStats] = useState({ total: 0, live: 0, scheduled: 0, expired: 0, inactive: 0, outOfStock: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [status, setStatus] = useState('');
  const [visibility, setVisibility] = useState('');
  const [view, setView] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const seq = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setLoading(true);
    setError('');
    try {
      const params = { page, limit: LIMIT, search: debounced, status, visibility };
      Object.keys(params).forEach((k) => params[k] === '' && delete params[k]);
      const res = await getAllCombos(params);
      if (mine !== seq.current) return;
      setRows(Array.isArray(res?.data) ? res.data : []);
      setMeta({ total: res?.meta?.total ?? res?.total ?? 0, totalPages: res?.meta?.totalPages || 1 });
      if (res?.stats) setStats(res.stats);
    } catch {
      if (mine === seq.current) setError('Could not load combo offers.');
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [page, debounced, status, visibility]);
  useEffect(() => { load(); }, [load]);

  const pickStatus = (s) => { setStatus(status === s ? '' : s); setPage(1); };
  const dirty = !!search || !!status || !!visibility;

  const toggle = async (c) => {
    try {
      await updateCombo(c._id, { isActive: !c.isActive });
      load();
    } catch { /* the service shows the error */ }
  };

  const confirmDelete = async () => {
    setDeleting(true);
    setDeleteError('');
    try {
      await deleteCombo(toDelete._id);
      setToDelete(null);
      setNotice('Combo deleted.');
      if (rows.length === 1 && page > 1) setPage(page - 1); else load();
    } catch (err) {
      setDeleteError(err?.message || 'Could not delete the combo.');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
        <Box>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>Combo offers</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 13.5, color: '#64748b' }}>Bundles of products sold together at one price.</Typography>
        </Box>
        <Button startIcon={<MdAdd />} variant="contained" onClick={() => navigate('/offers/combo/create')} sx={yellowBtn}>Add combo</Button>
      </Box>

      {notice && <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 2 }}>{notice}</Alert>}

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
        <Kpi title="All combos" value={stats.total} accent="#6366f1" active={!status} onClick={() => { setStatus(''); setPage(1); }} />
        {['live', 'scheduled', 'expired', 'inactive'].map((k) => <Kpi key={k} title={STATE[k].label} value={stats[k]} accent={STATE[k].accent} active={status === k} onClick={() => pickStatus(k)} />)}
        <Kpi title="Live but out of stock" value={stats.outOfStock} accent="#e11d48" />
      </Box>

      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2, p: 1.75, background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px' }}>
        <TextField size="small" placeholder="Search title, combo code or description…" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 240 }}
          InputProps={{ startAdornment: <InputAdornment position="start"><MdSearch size={18} /></InputAdornment> }} />
        <TextField select size="small" label="Status" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} sx={{ minWidth: 150 }}>
          <MenuItem value="">All</MenuItem>
          {Object.entries(STATE).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Customers" value={visibility} onChange={(e) => { setVisibility(e.target.value); setPage(1); }} sx={{ minWidth: 150 }}>
          <MenuItem value="">B2B and B2C</MenuItem><MenuItem value="b2c">Visible to B2C</MenuItem><MenuItem value="b2b">Visible to B2B</MenuItem>
        </TextField>
        {dirty && <Button onClick={() => { setSearch(''); setStatus(''); setVisibility(''); setPage(1); }} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Clear</Button>}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={head}>Combo</TableCell>
                <TableCell sx={head}>Products</TableCell>
                <TableCell sx={head}>Stock</TableCell>
                <TableCell sx={head} align="right">B2C price</TableCell>
                <TableCell sx={head}>Runs</TableCell>
                <TableCell sx={head}>Status</TableCell>
                <TableCell sx={head} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && [...Array(6)].map((_, i) => <TableRow key={i}>{[...Array(7)].map((__, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>)}
              {!loading && rows.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6, fontFamily: FONT, color: '#64748b' }}>No combos match these filters.</TableCell></TableRow>}
              {!loading && rows.map((c) => {
                const mrp = mrpOf(c);
                const price = c.b2c_sellingPrice;
                const save = mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;
                const first = c.product_images?.[0] || c.products?.[0]?.product_id?.product_images?.[0];
                return (
                  <TableRow key={c._id} hover>
                    <TableCell sx={{ ...cell, maxWidth: 300 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                        <Avatar variant="rounded" src={imageUrl(first)} sx={{ width: 42, height: 42, bgcolor: '#e0e7ff', color: '#3730a3' }}><MdInventory2 /></Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography noWrap onClick={() => navigate(`/offers/combo/view/${c._id}`)} sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5, cursor: 'pointer', '&:hover': { color: '#4338ca' } }} title={c.title}>{c.title}</Typography>
                          <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{c.comboCode}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={cell}>
                      <Chip size="small" clickable onClick={() => setView(c)} label={`${(c.products || []).length} item${(c.products || []).length === 1 ? '' : 's'}`} sx={{ fontFamily: FONT, fontWeight: 700, background: '#e0e7ff', color: '#3730a3' }} />
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={c.inStock ? `${c.availableQuantity} available` : 'Out of stock'} sx={{ fontFamily: FONT, fontWeight: 700, height: 22, background: c.inStock ? '#dcfce7' : '#fee2e2', color: c.inStock ? '#166534' : '#991b1b' }} />
                    </TableCell>
                    <TableCell align="right">
                      <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 14, color: '#166534' }}>{price ? inr(price) : '—'}</Typography>
                      {mrp > 0 && (
                        <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#94a3b8' }}>
                          <span style={{ textDecoration: 'line-through' }}>{inr(mrp)}</span>{save > 0 && <b style={{ color: '#b45309', marginLeft: 6 }}>{save}% off</b>}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ ...cell, whiteSpace: 'nowrap' }}>{period(c)}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Switch size="small" checked={!!c.isActive} onChange={() => toggle(c)} />
                        <StateChip state={stateOf(c)} />
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                      <Tooltip title="View"><IconButton size="small" onClick={() => navigate(`/offers/combo/view/${c._id}`)} sx={iconBtn('#4f46e5')}><MdVisibility size={17} /></IconButton></Tooltip>{' '}
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => navigate(`/offers/combo/edit/${c._id}`)} sx={iconBtn('#ca8a04')}><MdEdit size={17} /></IconButton></Tooltip>{' '}
                      <Tooltip title="Delete"><IconButton size="small" onClick={() => { setDeleteError(''); setToDelete(c); }} sx={iconBtn('#dc2626')}><MdDeleteOutline size={17} /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 1.5, borderTop: '1px solid #e2e8f0', flexWrap: 'wrap', gap: 1 }}>
          <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{meta.total} combo{meta.total === 1 ? '' : 's'}</Typography>
          <Pagination count={meta.totalPages} page={page} onChange={(_, p) => setPage(p)} shape="rounded" size="small" />
        </Box>
      </Box>

      {/* products popup */}
      <Dialog open={!!view} onClose={() => setView(null)} maxWidth="sm" fullWidth scroll="paper" PaperProps={{ sx: { borderRadius: '18px', background: '#f8fafc' } }}>
        {view && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 2.25, borderBottom: '3px solid #6366f1', background: '#fff' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography noWrap sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 19 }}>{view.title}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{view.comboCode} · {view.b2c_sellingPrice ? inr(view.b2c_sellingPrice) : 'No B2C price'}</Typography>
              </Box>
              <IconButton onClick={() => setView(null)} size="small"><MdClose size={20} /></IconButton>
            </Box>
            <DialogContent sx={{ p: 0 }}>
              {(view.products || []).map((p, i) => {
                const pr = p.product_id;
                const stock = pr?.availableQuantity ?? 0;
                const need = p.quantity || 1;
                return (
                  <Box key={pr?._id || i} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 1.25, borderBottom: '1px solid #f1f5f9', background: '#fff' }}>
                    <Avatar variant="rounded" src={imageUrl(pr?.product_images?.[0])} sx={{ width: 44, height: 44, bgcolor: '#e0e7ff', color: '#3730a3' }}><MdInventory2 /></Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 13.5 }}>{pr?.title || 'Product removed'}</Typography>
                      <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b' }}>{pr?.productCode || ''} · {stock} in stock · makes {Math.floor(stock / need)} combo(s)</Typography>
                    </Box>
                    <Chip size="small" label={`× ${need}`} sx={{ fontWeight: 800 }} />
                    {pr?.b2c_sellingPrice !== undefined && <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5, minWidth: 64, textAlign: 'right' }}>{inr(pr.b2c_sellingPrice)}</Typography>}
                  </Box>
                );
              })}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
              <Button onClick={() => setView(null)} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Close</Button>
              <Button onClick={() => navigate(`/offers/combo/view/${view._id}`)} variant="contained" sx={yellowBtn}>Open combo</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* delete */}
      <Dialog open={!!toDelete} onClose={() => !deleting && setToDelete(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 18, mb: 1 }}>Delete combo?</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#475569' }}><b>{toDelete?.title}</b> is removed from sale straight away. To pause it instead, turn its switch off.</Typography>
          {deleteError && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setToDelete(null)} disabled={deleting} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={confirmDelete} disabled={deleting} variant="contained" sx={{ textTransform: 'none', fontWeight: 800, background: '#dc2626', '&:hover': { background: '#b91c1c' } }}>{deleting ? 'Deleting…' : 'Delete'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ComboOfferList;
